import React, { useMemo } from "react";

import { createChangeEvent } from "../utils/form";

import OptionItem, { OptionItemProps } from "./OptionItem";

export interface OptionListProps {
  name: string;
  value?: string;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>, value: string) => void;
  /** You can specify either options or provide children of type OptionItem. */
  options?: Array<Pick<OptionItemProps, "label" | "value" | "icon" | "decorator" | "support" | "selected" | "disabled">>;
}

const OptionList: React.FC<React.PropsWithChildren<OptionListProps>> = ({
  name,
  value,
  disabled,
  onChange,
  options: rawOptions,
  children,
}) => {
  if (rawOptions && React.Children.count(children) > 0) {
    console.warn("You can specify either options or provide children of type OptionItem.");
  }

  const options = useMemo(() => {
    // If options are provided, create option items and use directly
    if (rawOptions) {
      return rawOptions.map((option) => (
        <OptionItem
          key={option.value}
          value={option.value}
          selected={option.value === value}
          disabled={option.disabled || disabled}
          icon={option.icon}
          decorator={option.decorator}
          label={option.label}
          support={option.support}
          onClick={() => onChange?.(createChangeEvent(name, option.value), option.value)}
        />
      ));
    }

    // If not, clone children and override the onClick props, as well as the disabled prop
    return React.Children.map(children, (child, i) => {
      const option = child as React.ReactElement<OptionItemProps>;
      // check if is right type
      if (option.type !== OptionItem) {
        console.warn(`Children of OptionList should be of type OptionItem (item ${i})`);
        // return null;
      }
      return React.cloneElement(option, {
        selected: option.props.value === value,
        disabled: option.props.disabled || disabled,
        onClick: () => onChange?.(
          // Fake change event
          createChangeEvent(name, option.props.value),
          option.props.value,
        ),
      });
    });
  }, [rawOptions, children, value, onChange]);

  return (
    <div className="py-1 shadow-sm rounded-md border border-border-base">
      {options}
    </div>
  );
};

export default OptionList;
